import {
  Component,
  computed,
  ElementRef,
  forwardRef,
  HostListener,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { injectVirtualizer } from '@tanstack/angular-virtual';
import { debounce, EMPTY, ReplaySubject, takeWhile, timer } from 'rxjs';
import { LookupOptionsService } from '../../../../features/book-claim/components/claims-form/services/lookup-options.service';
import { BeValuedLookupService } from '../../../../features/orbit/book-claim/services/bevalued-lookup.service';
import { OrbitLookupService } from '../../../../features/orbit/book-claim/services/orbit-lookup-service.service';
import { FirstLetterCapitalisePipe } from '../../../pipes/firstLetterCapitilise';
import { StringifyPipe } from '../../../pipes/stringify';
import { InputStatesService } from '../../../services/input-states.service';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { RadioButtonComponent } from '../radio-button/radio-button.component';
import { TextInputComponent } from '../text-input/text-input.component';

export interface Option {
  title: string;
  value: any;
}

@Component({
  selector: 'app-text-search',
  standalone: true,
  imports: [
    FormsModule,
    TextInputComponent,
    LoadingSpinnerComponent,
    RadioButtonComponent,
    StringifyPipe,
    FirstLetterCapitalisePipe,
  ],
  templateUrl: './text-search.component.html',
  styleUrl: './text-search.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => TextSearchComponent),
    },
  ],
})
export class TextSearchComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: MouseEvent) {
    if ((event.srcElement as HTMLElement).closest('app-text-search')) return;

    this.showOptions.set(false);
  }

  scrollElement = viewChild<ElementRef<HTMLDivElement>>('scrollElement');

  placeHolder = input('');
  label = input<string | undefined>();
  name = input.required<string>();
  searchIconPosition = input<'start' | 'end'>('end');
  selectedLabel = input<string>('Selected fault');
  useOrbitSearch = input<Record<string, any> | undefined>(undefined);
  useBVSearch = input<{ group: string; manufacturer: string } | undefined>(undefined);
  exampleText = input<string>('');
  textInputClasses = input<string>('');
  hideNoResultsHint = input<boolean>(false);
  errors = input<string | undefined>(undefined);
  initialApiError = input<boolean>(false);
  apiError = signal(false);

  loadingData = output<boolean>();
  loadingError = output<any>();
  noResults = output<void>();
  searchTermChanged = output<string>();
  apiLookupError = output<boolean>();

  options = signal<Option[]>([]);
  showOptions = signal(false);
  loading = signal(false);

  searchTerm = '';
  private _destroyed = false;
  value: any;
  touched = false;
  disabled = false;

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  searchChanged = new ReplaySubject(1);

  private readonly viewModel = computed(() => {
    return {
      label: this.label(),
      name: this.name(),
      placeHolder: this.placeHolder(),
      loading: this.loading(),
      options: this.options(),
      showOptions: this.showOptions(),
      selectedLabel: this.selectedLabel(),
      mappedValues: this.useOrbitSearch()
        ? this.orbitLookupOptions.loadedInValues()[this.name().toLowerCase()]
        : (this.lookupOptions.loadedValuesMappedIntoObject()[this.name()] ?? {}),
      valueForMappedLookup: (this.value as Record<string, any>)?.['dataLabel'] ?? this.value,
      searchIconPosition: this.searchIconPosition(),
      exampleText: this.exampleText(),
      textInputClasses: this.textInputClasses(),
      errors: this.errors(),
      hideNoResultsHint: this.hideNoResultsHint(),
    };
  });

  get vm() {
    return this.viewModel();
  }

  virtualiser = injectVirtualizer(() => ({
    scrollElement: this.scrollElement(),
    count: this.options().length,
    estimateSize: () => 60,
    overscan: 7,
  }));

  constructor(
    private readonly inputStateService: InputStatesService,
    private readonly lookupOptions: LookupOptionsService,
    private readonly orbitLookupOptions: OrbitLookupService,
    private readonly beValuedLookupOptions: BeValuedLookupService
  ) {}

  ngOnDestroy(): void {
    this._destroyed = true;
  }

  ngOnInit() {
    this.searchTerm = this.inputStateService.getStoredSearchTerm(this.vm.name);
    if (this.initialApiError()) {
      this.apiError.set(true);
    }
    this.loadDataFromSearch();
  }

  writeValue(value: any): void {
    this.value = value;
    this.onChange(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  loadDataFromSearch(): void {
    // Use a debounce to prevent spam of the endpoints
    this.searchChanged
      .asObservable()
      .pipe(
        debounce(() => timer(1000)),
        takeWhile(() => !this._destroyed)
      )
      .subscribe({
        next: () => {
          if (!this.searchTerm || this.searchTerm.trim().length < 3) {
            this.clearOptionsAndStopLoading();
            return;
          }

          let query;

          if (this.useOrbitSearch()) {
            const config = this.useOrbitSearch()!;
            query = this.orbitLookupOptions.lookupOptionsForStep(config, this.searchTerm);
          } else if (this.useBVSearch()) {
            const config = this.useBVSearch()!;
            query = this.beValuedLookupOptions.lookupOptionsForStep(config, this.searchTerm);
          } else {
            query = this.lookupOptions.lookupOptionsForStep(this.vm.name, this.searchTerm);
          }

          query.subscribe({
            next: (value) => {
              this.apiError.set(false);
              if (!value) {
                value = [];
              }

              if (JSON.stringify(this.vm.options) !== JSON.stringify(value)) {
                this.options.set(value);
              }

              this.loading.set(false);
              this.loadingData.emit(false);

              if (value.length === 0) {
                this.noResults.emit();
              }
            },
            error: (error) => {
              this.apiError.set(true);
              if (this.useBVSearch()) {
                this.searchTermChanged.emit(this.searchTerm);
              }
              this.apiLookupError.emit(true);
              this.loading.set(false);
              this.loadingData.emit(false);
              this.loadingError.emit(error);
            },
          });
        },
      });
  }

  onSearchChange(event: string) {
    this.searchTerm = event || '';
    this.inputStateService.storeSearchTerm(this.vm.name, this.searchTerm);

    this.searchTermChanged.emit(this.searchTerm);

    if (this.apiError()) {
      return;
    }

    if (this.searchTerm.trim().length < 3) {
      this.clearOptionsAndStopLoading();
      return;
    }

    this.markAsTouched();
    this.loading.set(true);
    this.loadingData.emit(true);
    this.searchChanged.next(EMPTY);
  }

  hideOptions(): void {
    setTimeout(() => {
      this.showOptions.set(false);
    });
  }

  handleRadioSelection(value: undefined | string): void {
    if (value) {
      value = JSON.parse(value);
    }
    this.writeValue(value);
  }

  clearOptionsAndStopLoading(): void {
    this.options.set([]);
    this.loading.set(false);
    this.loadingData.emit(false);
  }
}
